import React, { useState } from "react";
import { Button, View, Text, InputField, Badge, Card, Tooltip, Icon, DropdownField } from "../components/base";
import { PageLayout } from "../components/PageLayout";
import { ProviderLogo } from "../components/ProviderLogo";
import { makeRequest } from "../utils/request";

interface VoiceProfile {
  publicId: string;
  name: string;
  provider: string;
  gender: string;
  primaryLanguage: string;
  accent?: string;
  region?: string;
  voiceStyle?: string;
  description?: string;
  tags?: string[];
  useCases?: string[];
  soc2Attested?: boolean;
  similarity?: number;
}

interface VoiceSearchFilters {
  provider?: string;
  gender?: string;
  primaryLanguage?: string;
  accent?: string;
}

// Provider configuration for the explorer
interface ProviderConfig {
  label: string;
  idLabel: string;
  idPlaceholder: string;
  idHelp: string;
  exampleVoiceId: string;
  exampleVoiceName: string;
  voiceLibraryUrl: string;
}

const PROVIDER_CONFIG: Record<string, ProviderConfig> = {
  openai: {
    label: "OpenAI",
    idLabel: "Voice Name",
    idPlaceholder: "e.g., alloy, echo, shimmer, nova, fable, onyx, sage, ash, coral",
    idHelp: "OpenAI uses voice names as identifiers",
    exampleVoiceId: "alloy",
    exampleVoiceName: "Alloy",
    voiceLibraryUrl: "https://platform.openai.com/docs/guides/text-to-speech#voice-options",
  },
  cartesia: {
    label: "Cartesia",
    idLabel: "Voice ID",
    idPlaceholder: "e.g., a0e99841-438c-4a64-b679-ae501e7d6091",
    idHelp: "UUID from Cartesia's voice library",
    exampleVoiceId: "a0e99841-438c-4a64-b679-ae501e7d6091",
    exampleVoiceName: "Barbershop Man",
    voiceLibraryUrl: "https://play.cartesia.ai/voices",
  },
  azure: {
    label: "Azure",
    idLabel: "Voice Name",
    idPlaceholder: "e.g., en-US-JennyNeural, zh-CN-XiaoxiaoNeural",
    idHelp: "Full Azure voice name including locale",
    exampleVoiceId: "en-US-JennyNeural",
    exampleVoiceName: "Jenny (en-US)",
    voiceLibraryUrl: "https://speech.microsoft.com/portal/voicegallery",
  },
  google: {
    label: "Google",
    idLabel: "Voice Name",
    idPlaceholder: "e.g., en-US-Wavenet-D, en-GB-Neural2-A",
    idHelp: "Google Cloud TTS voice name",
    exampleVoiceId: "en-US-Wavenet-D",
    exampleVoiceName: "Wavenet D (en-US)",
    voiceLibraryUrl: "https://docs.cloud.google.com/text-to-speech/docs/list-voices-and-types#chirp3_hd_voices",
  },
  minimax: {
    label: "Minimax",
    idLabel: "Voice ID",
    idPlaceholder: "e.g., male-qn-qingse, female-shaonv",
    idHelp: "Minimax voice identifier",
    exampleVoiceId: "male-qn-qingse",
    exampleVoiceName: "Qingse (Male)",
    voiceLibraryUrl: "https://www.minimax.io/audio/voices",
  },
  fish_audio: {
    label: "Fish Audio",
    idLabel: "Voice ID",
    idPlaceholder: "e.g., d13f84b987ad4f22b56d2b47f4eb838e",
    idHelp: "Fish Audio reference ID",
    exampleVoiceId: "d13f84b987ad4f22b56d2b47f4eb838e",
    exampleVoiceName: "AD (Male)",
    voiceLibraryUrl: "https://fish.audio/app/discovery/",
  },
  inworld: {
    label: "Inworld",
    idLabel: "Voice ID",
    idPlaceholder: "e.g., en_us_male_1",
    idHelp: "Inworld voice identifier",
    exampleVoiceId: "Alex",
    exampleVoiceName: "Alex",
    voiceLibraryUrl: "https://platform.inworld.ai/v2/workspaces/default-7u0qvx5hfdes2alxhhqomg/voice-library",
  },
  gradium: {
    label: "Gradium",
    idLabel: "Voice ID",
    idPlaceholder: "e.g., YTpq7expH9539ERJ, LFZvm12tW_z0xfGo",
    idHelp: "Gradium voice identifier",
    exampleVoiceId: "YTpq7expH9539ERJ",
    exampleVoiceName: "Emma (en-US)",
    voiceLibraryUrl: "https://us.api.gradium.ai/studio/voices/library",
  },
  qwen3: {
    label: "Qwen3",
    idLabel: "Voice Name",
    idPlaceholder: "e.g., Cherry, Serena, Ethan, Chelsie, Ryan, Aiden",
    idHelp: "Qwen3 TTS voice name (case-sensitive)",
    exampleVoiceId: "Cherry",
    exampleVoiceName: "Cherry",
    voiceLibraryUrl: "https://www.alibabacloud.com/help/en/model-studio/qwen-tts-realtime",
  },
  elevenlabs: {
    label: "ElevenLabs",
    idLabel: "Voice ID",
    idPlaceholder: "e.g., 21m00Tcm4TlvDq8ikWAM",
    idHelp: "ElevenLabs voice ID from the voice library",
    exampleVoiceId: "21m00Tcm4TlvDq8ikWAM",
    exampleVoiceName: "Rachel",
    voiceLibraryUrl: "https://elevenlabs.io/app/voice-library",
  },
};

const TABS = [
  { id: "explorer", label: "Voice Lab", icon: "testing" },
  { id: "library", label: "Voice Library", icon: "grid" },
];

export const VoiceLibraryPage = () => {
  const [activeTab, setActiveTab] = useState("explorer");

  return (
    <PageLayout
      title="Voice Library"
      description="Test providers directly with custom voice IDs or search for voices using natural language."
      tabs={TABS}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      {activeTab === "library" ? <VoiceLibraryTab /> : <ProviderExplorerTab />}
    </PageLayout>
  );
};

// ─── Voice Library Tab ──────────────────────────────────────────────────────────

const VoiceLibraryTab = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [allVoices, setAllVoices] = useState<VoiceProfile[]>([]);
  const [searchResults, setSearchResults] = useState<VoiceProfile[]>([]);
  const [displayedVoices, setDisplayedVoices] = useState<VoiceProfile[]>([]);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [filters, setFilters] = useState<VoiceSearchFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [previewModal, setPreviewModal] = useState<{ isOpen: boolean; voice?: VoiceProfile }>({ isOpen: false });
  const [previewText, setPreviewText] = useState(
    "Hello, this is a preview of my voice. I can help you with various tasks and speak in a natural, friendly tone.",
  );
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [generatingAudio, setGeneratingAudio] = useState(false);

  const getGenderBadgeClass = (gender: string) => {
    const isActiveFilter = filters.gender && filters.gender.toLowerCase() === gender.toLowerCase();
    return isActiveFilter ? "bg-pink-500 text-white hover:bg-pink-600 border-transparent" : "bg-slate-500 text-white hover:bg-slate-600 border-transparent";
  };

  const getLanguageBadgeClass = (language: string) => {
    const activeLanguage = filters.primaryLanguage;
    if (!activeLanguage) {
      return "bg-slate-500 text-white hover:bg-slate-600 border-transparent";
    }
    const languageMapping: Record<string, string> = { english: "en", chinese: "zh", spanish: "es" };
    const filterValue = languageMapping[activeLanguage.toLowerCase()] || activeLanguage.toLowerCase();
    const voiceLanguage = languageMapping[language.toLowerCase()] || language.toLowerCase();
    const isActiveFilter = filterValue === voiceLanguage;
    return isActiveFilter ? "bg-pink-500 text-white hover:bg-pink-600 border-transparent" : "bg-slate-500 text-white hover:bg-slate-600 border-transparent";
  };

  const providerOptions = ["openai", "azure", "google", "cartesia", "prim", "minimax", "fish_audio", "inworld", "gradium", "elevenlabs"];
  const genderOptions = ["male", "female"];
  const languageOptions = ["English", "Chinese", "Spanish"];

  const applyClientSideFilters = (voices: VoiceProfile[]): VoiceProfile[] => {
    return voices.filter((voice) => {
      if (filters.provider && voice.provider.toLowerCase() !== filters.provider.toLowerCase()) return false;
      if (filters.gender && voice.gender.toLowerCase() !== filters.gender.toLowerCase()) return false;
      if (filters.primaryLanguage) {
        const languageMapping: Record<string, string> = { english: "en", chinese: "zh", spanish: "es" };
        const filterValue = languageMapping[filters.primaryLanguage.toLowerCase()] || filters.primaryLanguage.toLowerCase();
        const voiceLanguage = languageMapping[voice.primaryLanguage.toLowerCase()] || voice.primaryLanguage.toLowerCase();
        if (filterValue !== voiceLanguage) return false;
      }
      if (filters.accent && !voice.accent?.toLowerCase().includes(filters.accent.toLowerCase())) return false;
      return true;
    });
  };

  const loadAllVoices = async () => {
    setInitialLoading(true);
    try {
      const response = await makeRequest<{ data: VoiceProfile[] }>("/v1/voice-search");
      setAllVoices(response.data);
      setDisplayedVoices(response.data);
    } catch (error) {
      console.error("Error loading voices:", error);
    } finally {
      setInitialLoading(false);
    }
  };

  React.useEffect(() => {
    if (isSearchActive && searchResults.length > 0) {
      const filtered = applyClientSideFilters(searchResults);
      setDisplayedVoices(filtered);
    } else if (!isSearchActive && allVoices.length > 0) {
      const filtered = applyClientSideFilters(allVoices);
      setDisplayedVoices(filtered);
    }
  }, [filters, allVoices, searchResults, isSearchActive]);

  React.useEffect(() => {
    loadAllVoices();
  }, []);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setIsSearchActive(false);
      setSearchResults([]);
      const filtered = applyClientSideFilters(allVoices);
      setDisplayedVoices(filtered);
      return;
    }
    setLoading(true);
    setIsSearchActive(true);
    try {
      const params = new URLSearchParams();
      params.append("query", searchQuery);
      const response = await makeRequest<{ data: VoiceProfile[] }>(`/v1/voice-search?${params.toString()}`);
      setSearchResults(response.data);
      const filtered = applyClientSideFilters(response.data);
      setDisplayedVoices(filtered);
    } catch (error) {
      console.error("Error searching voices:", error);
      setIsSearchActive(false);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleShowAllVoices = () => {
    setSearchQuery("");
    setIsSearchActive(false);
    setSearchResults([]);
    const filtered = applyClientSideFilters(allVoices);
    setDisplayedVoices(filtered);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSearch();
  };

  const clearFilters = () => setFilters({});

  const removeFilter = (filterKey: keyof VoiceSearchFilters) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[filterKey];
      return newFilters;
    });
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const handlePreview = (voice: VoiceProfile) => {
    setPreviewModal({ isOpen: true, voice });
    setAudioUrl(null);
  };

  const handleGeneratePreview = async () => {
    if (!previewModal.voice || !previewText.trim()) return;
    setGeneratingAudio(true);
    try {
      const response = await makeRequest<Blob>("/v1/tts", {
        method: "POST",
        body: { text: previewText, voice: previewModal.voice.name },
        blob: true,
      });
      if (response.size === 0) throw new Error("Empty audio response");
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      const url = URL.createObjectURL(response);
      setAudioUrl(url);
    } catch (error) {
      console.error("Failed to generate preview:", error);
      alert("Failed to generate audio preview. Please try again.");
    } finally {
      setGeneratingAudio(false);
    }
  };

  const closePreviewModal = () => {
    setPreviewModal({ isOpen: false });
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
  };

  return (
    <>
      <View className="flex flex-col gap-4 mt-6">
        {/* Search Bar and Controls */}
        <View className="flex flex-col sm:flex-row gap-2">
          <View className="flex-1">
            <InputField
              placeholder="Search for voices... (e.g., 'warm female voice for customer service')"
              value={searchQuery}
              onChange={(value) => setSearchQuery(value)}
              onKeyDown={handleKeyDown}
              className="w-full"
            />
          </View>
          <View className="flex flex-row items-center gap-2">
            <Button
              variant="info"
              size="auto"
              onClick={handleSearch}
              disabled={loading || !searchQuery.trim()}
            >
              <Icon
                name="search"
                size={14}
              />
              {loading ? "Searching..." : "Search"}
            </Button>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex flex-row items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors border ${
                showFilters
                  ? "bg-purple-50 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-800"
                  : "bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700"
              }`}
            >
              <Icon
                name="variants"
                size={14}
              />
              Filters
              {activeFilterCount > 0 && (
                <Badge
                  variant="default"
                  className="ml-0.5 text-[10px] bg-pink-500 text-white hover:bg-pink-600 border-transparent"
                >
                  {activeFilterCount}
                </Badge>
              )}
            </button>
            <Button
              variant="neutral"
              size="auto"
              onClick={handleShowAllVoices}
              disabled={loading || initialLoading}
            >
              Show All
            </Button>
            {activeFilterCount > 0 && (
              <button
                onClick={clearFilters}
                className="flex flex-row items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/50 transition-colors"
              >
                <Icon
                  name="close"
                  size={12}
                />
                Clear
              </button>
            )}
          </View>
        </View>

        {/* Active Filters */}
        {activeFilterCount > 0 && (
          <View className="flex flex-row flex-wrap gap-2">
            {Object.entries(filters).map(([key, value]) =>
              value ? (
                <Badge
                  key={key}
                  variant="default"
                  className="flex items-center gap-1 bg-pink-500 text-white hover:bg-pink-600 border-transparent"
                >
                  {key}: {value}
                  <button
                    onClick={() => removeFilter(key as keyof VoiceSearchFilters)}
                    className="ml-1 hover:text-red-200"
                  >
                    ×
                  </button>
                </Badge>
              ) : null,
            )}
          </View>
        )}

        {/* Filter Panel */}
        {showFilters && (
          <View className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700/50">
            <View className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <DropdownField
                label="Provider"
                labelClassName="text-xs font-medium text-gray-500 dark:text-gray-400"
                value={filters.provider || ""}
                onChange={(value) => setFilters((prev) => ({ ...prev, provider: value || undefined }))}
                placeholder="All Providers"
                options={[
                  { label: "All Providers", value: "" },
                  ...providerOptions.map((p) => ({
                    label: PROVIDER_CONFIG[p]?.label || p,
                    value: p,
                  })),
                ]}
              />
              <DropdownField
                label="Gender"
                labelClassName="text-xs font-medium text-gray-500 dark:text-gray-400"
                value={filters.gender || ""}
                onChange={(value) => setFilters((prev) => ({ ...prev, gender: value || undefined }))}
                placeholder="All Genders"
                options={[{ label: "All Genders", value: "" }, ...genderOptions.map((g) => ({ label: g.charAt(0).toUpperCase() + g.slice(1), value: g }))]}
              />
              <DropdownField
                label="Primary Language"
                labelClassName="text-xs font-medium text-gray-500 dark:text-gray-400"
                value={filters.primaryLanguage || ""}
                onChange={(value) => setFilters((prev) => ({ ...prev, primaryLanguage: value || undefined }))}
                placeholder="All Languages"
                tooltip="The native language this voice was trained on and sounds most natural speaking."
                options={[{ label: "All Languages", value: "" }, ...languageOptions.map((l) => ({ label: l, value: l }))]}
              />
              <View className="flex flex-col gap-1.5">
                <Text className="text-xs font-medium text-gray-500 dark:text-gray-400">Accent</Text>
                <InputField
                  placeholder="e.g., American, British"
                  value={filters.accent || ""}
                  onChange={(value) => setFilters((prev) => ({ ...prev, accent: value || undefined }))}
                />
              </View>
            </View>
          </View>
        )}
      </View>

      {/* Results */}
      <View className="flex flex-col gap-4 mt-4">
        {(loading || initialLoading) && (
          <View className="flex items-center justify-center py-12">
            <Text className="text-sm text-gray-500 dark:text-gray-400">{initialLoading ? "Loading voices..." : "Searching..."}</Text>
          </View>
        )}

        {!loading && !initialLoading && displayedVoices.length === 0 && (
          <View className="flex items-center justify-center py-12">
            <Text className="text-sm text-gray-500 dark:text-gray-400">No voices found. Try adjusting your search query or filters.</Text>
          </View>
        )}

        {!loading && !initialLoading && displayedVoices.length > 0 && (
          <>
            <View className="flex flex-row items-center justify-between">
              <Text className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">
                {displayedVoices.length} voice{displayedVoices.length !== 1 ? "s" : ""}
                {searchQuery && ` for "${searchQuery}"`}
              </Text>
            </View>

            <View className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {displayedVoices.map((voice) => (
                <Card
                  key={voice.publicId}
                  className="p-4 transition-all duration-200 hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600 group"
                >
                  {/* Header: name + provider */}
                  <View className="flex flex-row items-center justify-between gap-3 mb-3">
                    <Text className="text-sm font-medium text-gray-900 dark:text-white flex-1 min-w-0 truncate">{voice.name}</Text>
                    <View className="flex flex-col items-end gap-1 shrink-0">
                      <div
                        className={
                          voice.provider.toLowerCase() === "prim"
                            ? "[--logo-fill:theme(colors.brand)] dark:[--logo-fill:theme(colors.cyan.400)] [--logo-stroke:theme(colors.slate.900)] dark:[--logo-stroke:theme(colors.slate.600)] [--logo-stroke-width:5]"
                            : ""
                        }
                      >
                        <ProviderLogo
                          provider={voice.provider}
                          size={["google", "prim", "minimax"].includes(voice.provider.toLowerCase()) ? "md" : "sm"}
                        />
                      </div>
                      {voice.soc2Attested ? (
                        <Tooltip content="This voice provider has SOC 2 attestation">
                          <Badge
                            variant="success"
                            className="text-[10px]"
                          >
                            SOC 2
                          </Badge>
                        </Tooltip>
                      ) : (
                        <Tooltip content="SOC 2 attestation not confirmed for this provider">
                          <Badge
                            variant="warning"
                            className="text-[10px]"
                          >
                            SOC 2
                          </Badge>
                        </Tooltip>
                      )}
                      {voice.similarity && (
                        <Badge
                          variant="info"
                          className="text-[10px] bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800"
                        >
                          {Math.round(voice.similarity * 100)}%
                        </Badge>
                      )}
                    </View>
                  </View>

                  {/* Badges */}
                  <View className="flex flex-row flex-wrap gap-1.5 mb-3">
                    {voice.gender && (
                      <Badge
                        variant="default"
                        className={`text-[10px] ${getGenderBadgeClass(voice.gender)}`}
                      >
                        {voice.gender}
                      </Badge>
                    )}
                    {voice.primaryLanguage && (
                      <Badge
                        variant="default"
                        className={`text-[10px] ${getLanguageBadgeClass(voice.primaryLanguage)}`}
                      >
                        {voice.primaryLanguage}
                      </Badge>
                    )}
                  </View>

                  {/* Detail rows */}
                  <View className="flex flex-col gap-1.5 pt-2.5 border-t border-gray-100 dark:border-gray-800">
                    {voice.description && (
                      <View className="flex flex-row items-center gap-1.5">
                        <Icon
                          name="info"
                          size={12}
                          className="text-gray-400 shrink-0"
                        />
                        <span
                          className="text-[11px] text-gray-500 dark:text-gray-400 truncate"
                          title={voice.description}
                        >
                          {voice.description}
                        </span>
                      </View>
                    )}
                    {voice.voiceStyle && (
                      <View className="flex flex-row items-center gap-1.5">
                        <Icon
                          name="voice-over"
                          size={12}
                          className="text-purple-400 shrink-0"
                        />
                        <Text className="text-[11px] text-gray-500 dark:text-gray-400 capitalize">{voice.voiceStyle}</Text>
                      </View>
                    )}
                    {voice.accent && (
                      <View className="flex flex-row items-center gap-1.5">
                        <Icon
                          name="language"
                          size={12}
                          className="text-blue-400 shrink-0"
                        />
                        <Text className="text-[11px] text-gray-500 dark:text-gray-400 capitalize flex-1">{voice.accent}</Text>
                      </View>
                    )}
                  </View>

                  {/* Preview button */}
                  <View className="mt-3 pt-2.5 border-t border-gray-100 dark:border-gray-800">
                    <button
                      onClick={() => handlePreview(voice)}
                      className="flex flex-row items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 w-full justify-center"
                    >
                      <Icon
                        name="play"
                        size={12}
                      />
                      Preview
                    </button>
                  </View>
                </Card>
              ))}
            </View>
          </>
        )}
      </View>

      {/* Preview Modal */}
      {previewModal.isOpen && previewModal.voice && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl p-6 w-full max-w-md border border-gray-200 dark:border-gray-700 shadow-xl">
            <div className="flex flex-row items-center justify-between mb-4">
              <Text className="text-sm font-semibold text-gray-900 dark:text-white">{previewModal.voice.name}</Text>
              <button
                onClick={closePreviewModal}
                className="flex items-center justify-center w-7 h-7 rounded-lg text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <Icon
                  name="close"
                  size={16}
                />
              </button>
            </div>

            <div className="flex flex-row flex-wrap items-center gap-2 mb-4">
              <div
                className={
                  previewModal.voice.provider.toLowerCase() === "prim"
                    ? "flex items-center h-6 [--logo-fill:theme(colors.brand)] dark:[--logo-fill:theme(colors.cyan.400)] [--logo-stroke:theme(colors.slate.900)] dark:[--logo-stroke:theme(colors.slate.600)] [--logo-stroke-width:5]"
                    : "flex items-center h-6"
                }
              >
                <ProviderLogo
                  provider={previewModal.voice.provider}
                  size={["google", "prim", "minimax"].includes(previewModal.voice.provider.toLowerCase()) ? "md" : "sm"}
                />
              </div>
              <Badge
                variant="default"
                className={`text-[10px] ${getGenderBadgeClass(previewModal.voice.gender)}`}
              >
                {previewModal.voice.gender}
              </Badge>
              <Badge
                variant="default"
                className={`text-[10px] ${getLanguageBadgeClass(previewModal.voice.primaryLanguage)}`}
              >
                {previewModal.voice.primaryLanguage}
              </Badge>
            </div>

            <div className="flex flex-col gap-1.5 mb-4">
              <Text className="text-xs font-medium text-gray-500 dark:text-gray-400">Preview Text</Text>
              <textarea
                value={previewText}
                onChange={(e) => setPreviewText(e.target.value)}
                className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-sm text-gray-900 dark:text-white resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 dark:focus:border-blue-500 transition-colors"
                rows={3}
                placeholder="Enter text to preview..."
              />
            </div>

            <div className="flex flex-col gap-3">
              <Button
                variant="info"
                size="auto"
                onClick={handleGeneratePreview}
                disabled={generatingAudio || !previewText.trim()}
                className="justify-center w-full"
              >
                <Icon
                  name="play"
                  size={14}
                />
                {generatingAudio ? "Generating..." : "Generate Preview"}
              </Button>

              {audioUrl && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                  <audio
                    controls
                    src={audioUrl}
                    className="w-full"
                    autoPlay
                  >
                    Your browser does not support the audio element.
                  </audio>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ─── Provider Explorer Tab ──────────────────────────────────────────────────────

interface ProviderRow {
  id: string;
  provider: string;
  voiceId: string;
  status: "idle" | "generating" | "done" | "error";
  audioUrl?: string;
  audioBlob?: Blob;
  ttfaMs?: number;
  error?: string;
}

let rowIdCounter = 0;
const nextRowId = () => `row-${++rowIdCounter}`;

const PROVIDER_OPTIONS = Object.entries(PROVIDER_CONFIG).map(([key, config]) => ({
  label: config.label,
  value: key,
}));

const ProviderExplorerTab = () => {
  const [previewText, setPreviewText] = useState(
    "Hello, this is a preview of my voice. I can help you with various tasks and speak in a natural, friendly tone.",
  );
  const [rows, setRows] = useState<ProviderRow[]>([{ id: nextRowId(), provider: "openai", voiceId: "", status: "idle" }]);

  const addRow = () => {
    setRows((prev) => [...prev, { id: nextRowId(), provider: "openai", voiceId: "", status: "idle" }]);
  };

  const removeRow = (id: string) => {
    setRows((prev) => {
      const row = prev.find((r) => r.id === id);
      if (row?.audioUrl) URL.revokeObjectURL(row.audioUrl);
      return prev.filter((r) => r.id !== id);
    });
  };

  const updateRow = (id: string, updates: Partial<ProviderRow>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...updates } : r)));
  };

  const handleGenerate = async (rowId: string) => {
    const row = rows.find((r) => r.id === rowId);
    if (!row || !row.voiceId.trim() || !previewText.trim()) return;

    // Clean up previous audio
    if (row.audioUrl) URL.revokeObjectURL(row.audioUrl);
    updateRow(rowId, { status: "generating", audioUrl: undefined, audioBlob: undefined, ttfaMs: undefined, error: undefined });

    const startTime = performance.now();
    try {
      const response = await makeRequest<Blob>("/v1/tts/provider", {
        method: "POST",
        body: { text: previewText, provider: row.provider, voiceId: row.voiceId.trim() },
        blob: true,
      });
      const ttfaMs = Math.round(performance.now() - startTime);
      if (response.size === 0) throw new Error("Empty audio response");
      const audioUrl = URL.createObjectURL(response);
      updateRow(rowId, { status: "done", audioUrl, audioBlob: response, ttfaMs });
    } catch (err: any) {
      const ttfaMs = Math.round(performance.now() - startTime);
      const raw = err?.message || "Generation failed";
      const providerLabel = PROVIDER_CONFIG[row.provider]?.label || row.provider;
      let error = raw;
      if (/404|not found|invalid.*voice|unknown.*voice|does not exist/i.test(raw)) {
        error = `Voice ID "${row.voiceId}" was not found on ${providerLabel}. Check the ID and try again, or browse their voice library for valid IDs.`;
      } else if (/401|403|unauthorized|forbidden|auth/i.test(raw)) {
        error = `Authentication failed for ${providerLabel}. The API key may be missing or invalid.`;
      }
      updateRow(rowId, { status: "error", ttfaMs, error });
    }
  };

  const handleGenerateAll = async () => {
    const validRows = rows.filter((r) => r.voiceId.trim() && previewText.trim());
    if (validRows.length === 0) return;
    await Promise.allSettled(validRows.map((r) => handleGenerate(r.id)));
  };

  const handleDownload = (row: ProviderRow) => {
    if (!row.audioUrl || !row.audioBlob) return;
    const a = document.createElement("a");
    a.href = row.audioUrl;
    a.download = `${row.provider}_${row.voiceId}.wav`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const validCount = rows.filter((r) => r.voiceId.trim()).length;
  const anyGenerating = rows.some((r) => r.status === "generating");

  const ttfaColor = (ms: number) => (ms < 500 ? "text-emerald-400" : ms < 1500 ? "text-amber-400" : "text-red-400");

  const ttfaBg = (ms: number) =>
    ms < 500 ? "bg-emerald-500/10 border-emerald-500/20" : ms < 1500 ? "bg-amber-500/10 border-amber-500/20" : "bg-red-500/10 border-red-500/20";

  return (
    <View className="flex flex-col gap-6 mt-6">
      {/* Prompt input */}
      <View className="flex flex-col gap-1.5">
        <Text className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">Input Text</Text>
        <div className="relative">
          <textarea
            value={previewText}
            onChange={(e) => setPreviewText(e.target.value)}
            className="w-full p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800/60 text-sm text-gray-900 dark:text-white resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 dark:focus:border-blue-500 transition-colors leading-relaxed placeholder:text-gray-400 dark:placeholder:text-gray-600"
            rows={2}
            placeholder="Type or paste the text you want synthesized..."
          />
          <div className="absolute bottom-3 right-4 text-[10px] font-mono text-gray-300 dark:text-gray-600 pointer-events-none">{previewText.length} chars</div>
        </div>
      </View>

      {/* Voices header */}
      <Text className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">Voices</Text>

      {/* Voice rows */}
      <View className="flex flex-col gap-3">
        {rows.map((row) => {
          const config = PROVIDER_CONFIG[row.provider];
          const canGenerateRow = row.voiceId.trim() && previewText.trim() && row.status !== "generating";
          return (
            <View
              key={row.id}
              className="group rounded-lg border border-gray-200 dark:border-gray-700/50 bg-white dark:bg-gray-800/30 p-4"
            >
              {/* Provider header */}
              <View className="flex flex-row items-center gap-3 mb-3">
                <ProviderLogo
                  provider={row.provider}
                  size="md"
                />
                <View className="w-[170px] shrink-0">
                  <DropdownField
                    value={row.provider}
                    onChange={(value) =>
                      updateRow(row.id, {
                        provider: value,
                        voiceId: "",
                        status: "idle",
                        audioUrl: undefined,
                        audioBlob: undefined,
                        ttfaMs: undefined,
                        error: undefined,
                      })
                    }
                    options={PROVIDER_OPTIONS}
                  />
                </View>
                <div className="flex-1" />
                <button
                  onClick={() => removeRow(row.id)}
                  className="flex items-center justify-center w-7 h-7 rounded-md text-gray-300 dark:text-gray-600 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors opacity-0 group-hover:opacity-100"
                >
                  <Icon
                    name="close"
                    size={14}
                  />
                </button>
              </View>

              {/* Voice ID + Generate */}
              <View className="flex flex-row items-center gap-2">
                <View className="flex-1">
                  <InputField
                    placeholder={config?.idPlaceholder || "Voice ID"}
                    value={row.voiceId}
                    onChange={(value) => updateRow(row.id, { voiceId: value })}
                    className="w-full font-mono"
                  />
                </View>
                <Button
                  variant="info"
                  size="auto"
                  onClick={() => handleGenerate(row.id)}
                  disabled={!canGenerateRow}
                >
                  {row.status === "generating" ? (
                    <>
                      <Icon
                        name="loader"
                        size={14}
                        className="animate-spin"
                      />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Icon
                        name="play"
                        size={14}
                      />
                      Generate
                    </>
                  )}
                </Button>
              </View>

              {config && (
                <View className="flex flex-row items-center gap-3 mt-2 pl-0.5">
                  <button
                    onClick={() => updateRow(row.id, { voiceId: config.exampleVoiceId })}
                    className="text-[10px] text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors cursor-pointer"
                    title={`Use example: ${config.exampleVoiceName}`}
                  >
                    Try <span className="font-mono">{config.exampleVoiceName}</span>
                  </button>
                  <span className="text-gray-300 dark:text-gray-700">|</span>
                  <a
                    href={config.voiceLibraryUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-[10px] text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  >
                    Browse voices <span className="inline-block translate-y-[-1px]">&#8599;</span>
                  </a>
                </View>
              )}

              {/* Error result */}
              {row.status === "error" && (
                <View className="flex flex-row items-center gap-3 mt-3 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/30">
                  <Icon
                    name="error"
                    size={14}
                    className="text-red-500 shrink-0"
                  />
                  <Text className="text-xs text-red-600 dark:text-red-400 flex-1">{row.error}</Text>
                  {row.ttfaMs !== undefined && <span className="text-[10px] font-mono text-red-400/60">{(row.ttfaMs / 1000).toFixed(2)}s</span>}
                </View>
              )}

              {/* Audio result */}
              {row.status === "done" && row.audioUrl && (
                <View className="flex flex-row items-center gap-3 mt-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/30">
                  <audio
                    controls
                    src={row.audioUrl}
                    className="flex-1 h-8"
                  >
                    Your browser does not support the audio element.
                  </audio>
                  {row.ttfaMs !== undefined && (
                    <Tooltip content="Time to first audio">
                      <View
                        className={`flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-mono tabular-nums shrink-0 ${ttfaBg(row.ttfaMs)} ${ttfaColor(row.ttfaMs)}`}
                      >
                        <Icon
                          name="speed"
                          size={11}
                        />
                        {(row.ttfaMs / 1000).toFixed(2)}s
                      </View>
                    </Tooltip>
                  )}
                  <Tooltip content="Download WAV">
                    <button
                      onClick={() => handleDownload(row)}
                      className="flex items-center justify-center w-8 h-8 rounded-md text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-600 dark:hover:text-gray-300 transition-colors shrink-0"
                    >
                      <Icon
                        name="download"
                        size={16}
                      />
                    </button>
                  </Tooltip>
                </View>
              )}
            </View>
          );
        })}

        {/* Placeholder for adding next voice */}
        <button
          onClick={addRow}
          className="group/add rounded-lg border border-dashed border-gray-300 dark:border-gray-700/50 hover:border-gray-400 dark:hover:border-gray-600 bg-transparent hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors py-5 flex items-center justify-center gap-2"
        >
          <Icon
            name="add"
            size={14}
            className="text-gray-400 dark:text-gray-600 group-hover/add:text-gray-500 dark:group-hover/add:text-gray-400 transition-colors"
          />
          <span className="text-xs font-medium text-gray-400 dark:text-gray-600 group-hover/add:text-gray-500 dark:group-hover/add:text-gray-400 transition-colors">
            Add another voice
          </span>
        </button>
      </View>

      {/* Generate All */}
      {validCount > 1 && (
        <Button
          variant="info"
          size="auto"
          onClick={handleGenerateAll}
          disabled={anyGenerating || validCount === 0 || !previewText.trim()}
          className="justify-center w-full"
        >
          <Icon
            name="play"
            size={14}
          />
          {anyGenerating ? "Generating..." : `Generate All ${validCount} Voices`}
        </Button>
      )}
    </View>
  );
};
